import type { Id } from '@e-socna/data-layer';

import type { CheckboxListItem } from '../../../components/CheckboxList';

interface MissionFileCategoryLike {
  readonly _id: Id;
  readonly label?: string;
  readonly parentCategoryId?: Id | null;
}

export interface MissionFileCategoryTreeNode<
  T extends MissionFileCategoryLike,
> {
  readonly category: T;
  readonly children: readonly MissionFileCategoryTreeNode<T>[];
}

// A category whose parent isn't in the given list (filtered out, e.g.
// technical, or genuinely absent) is treated as a root - it must still be
// reachable, just without its real ancestry.
export const buildMissionFileCategoryTree = <T extends MissionFileCategoryLike>(
  categories: readonly T[],
): readonly MissionFileCategoryTreeNode<T>[] => {
  const categoryIds = new Set(categories.map((category) => category._id));
  const childrenByParentId = new Map<Id, T[]>();
  const roots: T[] = [];

  for (const category of categories) {
    const parentId = category.parentCategoryId;
    const hasVisibleParent = !!parentId && categoryIds.has(parentId);

    if (hasVisibleParent) {
      const siblings = childrenByParentId.get(parentId) ?? [];
      siblings.push(category);
      childrenByParentId.set(parentId, siblings);
    } else {
      roots.push(category);
    }
  }

  const buildNode = (category: T): MissionFileCategoryTreeNode<T> => ({
    category,
    children: (childrenByParentId.get(category._id) ?? []).map(buildNode),
  });

  return roots.map(buildNode);
};

export const toMissionFileCategoryCheckboxListItems = <
  T extends MissionFileCategoryLike,
>(
  nodes: readonly MissionFileCategoryTreeNode<T>[],
): readonly CheckboxListItem<T['_id']>[] =>
  nodes.map(({ category, children }) => ({
    children: toMissionFileCategoryCheckboxListItems(children),
    id: category._id,
    label: category.label ?? '',
  }));
